// AI-assisted. Reviewed by: [name] on [date]. Tests: ✅. No unreviewed auth changes.
// T018: Wired global search bar to GET /search?q= (Feature 034-dashboard-search-filter-owner-fixes)
import { useState, useRef, useEffect } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  BellIcon,
  UsersIcon,
  CommandLineIcon,
  LinkIcon,
  DocumentIcon,
  EnvelopeIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useRequireRole } from '@/services/auth';
import { useScopeStore } from '@/features/scope/useScopeStore';
import { getSearch } from '@/services/api';
import type { GlobalSearchResponse } from '@/types/search';

const ALL_USER_ITEMS = [
  { label: 'Marketing Materials', to: '/marketing-materials', icon: DocumentIcon },
] as const;

const ADMIN_ONLY_ITEMS = [
  { label: 'Users', to: '/admin/users', icon: UsersIcon },
  { label: 'Prompts', to: '/admin/prompts', icon: CommandLineIcon },
  { label: 'Integrations', to: '/admin/integrations', icon: LinkIcon },
  { label: 'Email Templates', to: '/admin/email-templates', icon: EnvelopeIcon },
] as const;

const ROLE_LABELS: Record<1 | 2 | 3 | 4 | 5 | 6, string> = { 1: 'Employee', 2: 'Manager', 3: 'Admin', 4: 'Partners Manager', 5: 'Catalog Manager', 6: 'Orders Manager' };

export default function AppTopBar() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isManagerOrAbove = useRequireRole([2, 3]);
  const { scope, setScope } = useScopeStore();

  // T018: Global search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: searchResults } = useQuery<GlobalSearchResponse>({
    queryKey: ['search', searchQuery],
    queryFn: () => getSearch(searchQuery),
    enabled: searchQuery.trim().length >= 1,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasResults = searchResults &&
    (searchResults.clients.length > 0 || searchResults.leads.length > 0 || searchResults.tickets.length > 0);

  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';

  return (
    <header className="flex items-center gap-4 px-5 py-3 bg-brand-surface-card border-b border-brand-border">
      {/* Scope toggle */}
      <div className="flex items-center rounded-lg border border-brand-border overflow-hidden text-sm">
        <button
          type="button"
          onClick={() => setScope('mine')}
          className={`px-3 py-1.5 transition-colors ${
            scope === 'mine'
              ? 'bg-brand-accent text-brand-text-inverse font-medium'
              : 'text-brand-text-secondary hover:bg-brand-surface'
          }`}
        >
          My Clients
        </button>
        <button
          type="button"
          onClick={() => setScope('team')}
          className={`px-3 py-1.5 transition-colors ${
            scope === 'team'
              ? 'bg-brand-accent text-brand-text-inverse font-medium'
              : 'text-brand-text-secondary hover:bg-brand-surface'
          }`}
        >
          All Team Data
        </button>
      </div>

      {/* Search */}
      <div className="flex-1 relative max-w-md" ref={searchRef}>
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-secondary pointer-events-none" />
        <input
          type="search"
          placeholder="Search clients, leads, tickets…"
          aria-label="Global search"
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={showResults && searchQuery.trim().length >= 1}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-brand-surface border border-brand-border rounded-full outline-none focus:border-brand-filter-tab-active-bg"
        />

        {/* Search results dropdown */}
        {showResults && searchQuery.trim().length >= 1 && (
          <div
            id="search-results"
            role="listbox"
            aria-label="Search results"
            className="absolute top-full mt-1 left-0 right-0 z-50 bg-brand-surface-card border border-brand-border rounded-lg shadow-lg max-h-96 overflow-y-auto"
          >
            {!hasResults ? (
              <p className="px-4 py-3 text-sm text-brand-text-secondary">No results found.</p>
            ) : (
              <>
                {searchResults!.clients.length > 0 && (
                  <section aria-label="Clients">
                    <p className="px-3 py-1.5 text-xs font-semibold text-brand-text-secondary uppercase tracking-wide border-b border-brand-border">
                      Clients
                    </p>
                    {searchResults!.clients.map((c) => (
                      <button
                        key={c.accountId}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => { navigate(`/clients/${c.accountId}`); setShowResults(false); setSearchQuery(''); }}
                        className="flex flex-col w-full px-4 py-2 text-left text-sm hover:bg-brand-surface focus:bg-brand-surface outline-none"
                      >
                        <span className="font-medium text-brand-text-primary">{c.companyName ?? '—'}</span>
                        {c.contactName && <span className="text-xs text-brand-text-secondary">{c.contactName}</span>}
                      </button>
                    ))}
                  </section>
                )}

                {searchResults!.leads.length > 0 && (
                  <section aria-label="Leads">
                    <p className="px-3 py-1.5 text-xs font-semibold text-brand-text-secondary uppercase tracking-wide border-b border-brand-border">
                      Leads
                    </p>
                    {searchResults!.leads.map((l) => (
                      <button
                        key={l.leadId}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => { navigate(`/leads/${l.leadId}`); setShowResults(false); setSearchQuery(''); }}
                        className="flex flex-col w-full px-4 py-2 text-left text-sm hover:bg-brand-surface focus:bg-brand-surface outline-none"
                      >
                        <span className="font-medium text-brand-text-primary">{l.subject}</span>
                        {l.companyName && <span className="text-xs text-brand-text-secondary">{l.companyName}</span>}
                      </button>
                    ))}
                  </section>
                )}

                {searchResults!.tickets.length > 0 && (
                  <section aria-label="Tickets">
                    <p className="px-3 py-1.5 text-xs font-semibold text-brand-text-secondary uppercase tracking-wide border-b border-brand-border">
                      Tickets
                    </p>
                    {searchResults!.tickets.map((t) => (
                      <button
                        key={t.ticketId}
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => { navigate(`/tickets/${t.ticketId}`); setShowResults(false); setSearchQuery(''); }}
                        className="flex flex-col w-full px-4 py-2 text-left text-sm hover:bg-brand-surface focus:bg-brand-surface outline-none"
                      >
                        <span className="font-medium text-brand-text-primary">{t.subject}</span>
                        {t.clientName && <span className="text-xs text-brand-text-secondary">{t.clientName}</span>}
                      </button>
                    ))}
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Right cluster */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Bell */}
        <button
          type="button"
          aria-label="Notifications"
          className="relative p-1.5 rounded-full hover:bg-brand-surface text-brand-text-secondary"
        >
          <BellIcon className="h-5 w-5" />
          <span
            className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-brand-priority-urgent"
            aria-hidden="true"
          />
        </button>

        {/* Settings gear — all authenticated users */}
        {!!user && (
          <Menu as="div" className="relative">
            <MenuButton
              aria-label="Settings"
              className="p-1.5 rounded hover:bg-brand-surface text-brand-text-secondary"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </MenuButton>

            <MenuItems
              anchor="bottom end"
              className="absolute right-0 mt-1 w-48 bg-brand-surface-card border border-brand-border rounded-lg shadow-lg py-1 z-50 focus:outline-none"
            >
              {ALL_USER_ITEMS.map(({ label, to, icon: Icon }) => (
                <MenuItem key={label}>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={() => navigate(to)}
                      className={`flex items-center gap-3 w-full px-4 py-2 text-sm ${
                        focus ? 'bg-brand-surface text-brand-text-primary' : 'text-brand-text-primary'
                      }`}
                    >
                      <Icon className="h-4 w-4 text-brand-text-secondary" />
                      {label}
                    </button>
                  )}
                </MenuItem>
              ))}

              {isManagerOrAbove && ADMIN_ONLY_ITEMS.map(({ label, to, icon: Icon }) => (
                <MenuItem key={label}>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={() => navigate(to)}
                      className={`flex items-center gap-3 w-full px-4 py-2 text-sm ${
                        focus ? 'bg-brand-surface text-brand-text-primary' : 'text-brand-text-primary'
                      }`}
                    >
                      <Icon className="h-4 w-4 text-brand-text-secondary" />
                      {label}
                    </button>
                  )}
                </MenuItem>
              ))}

              {/* Divider */}
              <div className="my-1 border-t border-brand-border" />

              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={signOut}
                    className={`flex items-center gap-3 w-full px-4 py-2 text-sm ${
                      focus ? 'bg-brand-surface text-brand-priority-urgent' : 'text-brand-priority-urgent'
                    }`}
                  >
                    <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                    Sign Out
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Menu>
        )}

        {/* User avatar + name + role */}
        <div className="flex items-center gap-2">
          <div
            className="h-8 w-8 rounded-full bg-brand-filter-tab-active-bg flex items-center justify-center text-xs font-bold text-brand-filter-tab-active-text flex-shrink-0"
            aria-hidden="true"
          >
            {initials}
          </div>
          {user?.displayName && (
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-sm font-medium text-brand-text-primary">{user.displayName}</span>
              {user.role && (
                <span className="text-xs text-brand-text-secondary">{ROLE_LABELS[user.role]}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
